/** Compact conversation digest for archive / Continue (#450). Navigation aid only. */

export interface DigestDecision { readonly text: string; readonly sourceId: string }
export interface DigestOpenItem { readonly text: string; readonly sourceId: string }
export interface DigestReference { readonly kind: string; readonly id: string; readonly label: string }

export interface ConversationDigest {
  readonly version: 1;
  readonly title: string;
  readonly decisions: readonly DigestDecision[];
  readonly openItems: readonly DigestOpenItem[];
  readonly references: readonly DigestReference[];
  readonly sourceCommandIds: readonly string[];
  readonly sourcePlanIds: readonly string[];
  readonly sourceResultIds: readonly string[];
}

export interface DigestTurn {
  readonly id: string;
  readonly mine: boolean;
  readonly text: string;
  readonly seq: number | null;
  readonly kind?: string | null;
  readonly planId?: string | null;
  readonly resultId?: string | null;
}

function clip(text: string, max: number): string {
  const one = text.replace(/\s+/g, ' ').trim();
  if (one.length <= max) return one;
  return `${one.slice(0, Math.max(0, max - 1))}…`;
}

export function titleFromFirstOwnerGoal(turns: readonly DigestTurn[]): string {
  const first = turns.find((turn) => turn.mine && turn.text.trim() !== '');
  if (first === undefined) return 'Untitled chat';
  return clip(first.text, 72);
}

export function buildConversationDigest(input: {
  readonly turns: readonly DigestTurn[];
  readonly title?: string | null;
}): ConversationDigest {
  const title = (input.title && input.title.trim() !== '')
    ? clip(input.title, 120)
    : titleFromFirstOwnerGoal(input.turns);

  const decisions: DigestDecision[] = [];
  const openItems: DigestOpenItem[] = [];
  const references: DigestReference[] = [];
  const sourceCommandIds: string[] = [];
  const sourcePlanIds: string[] = [];
  const sourceResultIds: string[] = [];

  for (const turn of input.turns) {
    if (turn.mine && turn.seq !== null) sourceCommandIds.push(turn.id);
    if (turn.planId) {
      sourcePlanIds.push(turn.planId);
      references.push({ kind: 'plan', id: turn.planId, label: 'plan' });
    }
    if (turn.resultId) {
      sourceResultIds.push(turn.resultId);
      references.push({ kind: 'result', id: turn.resultId, label: 'result' });
    }
    const text = clip(turn.text, 180);
    if (text === '') continue;
    if (!turn.mine && /approved|rejected|decided|will|plan/i.test(text)) {
      decisions.push({ text, sourceId: turn.id });
    } else if (turn.mine && /\?|blocked|waiting|need|should we/i.test(text)) {
      openItems.push({ text, sourceId: turn.id });
    }
  }

  return {
    version: 1,
    title,
    decisions: decisions.slice(0, 8),
    openItems: openItems.slice(0, 8),
    references: references.slice(0, 16),
    sourceCommandIds: sourceCommandIds.slice(-16),
    sourcePlanIds: [...new Set(sourcePlanIds)].slice(-16),
    sourceResultIds: [...new Set(sourceResultIds)].slice(-16),
  };
}

export function redactDigestSecrets(digest: ConversationDigest): ConversationDigest {
  const scrub = (value: string): string =>
    value
      .replace(/\b(sk|rk|key|token|secret|password|api[_-]?key)[=: ]+\S+/gi, '$1=[redacted]')
      .replace(/\b[A-Za-z0-9_-]{32,}\b/g, '[redacted]');
  return {
    ...digest,
    title: scrub(digest.title),
    decisions: digest.decisions.map((item) => ({ ...item, text: scrub(item.text) })),
    openItems: digest.openItems.map((item) => ({ ...item, text: scrub(item.text) })),
  };
}
